import type { LinkingOptions } from '@react-navigation/native';
import type { RootStackParamList } from './RootNavigator';

/**
 * CARTE DES URL — WEB UNIQUEMENT, et cette restriction est le point le plus important du fichier.
 *
 * POURQUOI PAS SUR MOBILE. `linking` est le mécanisme que react-navigation utilise AUSSI pour les
 * deep links natifs. Le brancher globalement ferait passer le retour d'authentification mobile
 * — confirmation d'e-mail, retour OAuth — par un chemin neuf, alors qu'il vient d'être réparé deux
 * fois et qu'il est tenu aujourd'hui par `useAuthDeepLink`. On ne rouvre pas ce chemin pour un
 * confort web. `RootNavigator` ne passe donc cette configuration que sous la garde `SUR_LE_WEB`,
 * et sur mobile la prop reste `undefined` : rien ne change, littéralement.
 *
 * LES ÉTAPES BLOQUANTES ONT UNE ADRESSE — ET C'EST L'INVERSE DE CE QUE CE FICHIER DISAIT D'ABORD.
 *
 * `AcceptTerms`, `ProfileCompletion` et `Splash` n'en avaient pas, au motif qu'une adresse
 * permettrait de les atteindre par un lien. Ce raisonnement était FAUX sur les deux moitiés.
 *
 * Il ne protégeait rien : ce qui rend ces écrans inatteignables par une URL, c'est que
 * `RootNavigator` ne les MONTE que lorsque la porte l'exige. Une adresse ne peut pas changer l'état
 * de la porte ; elle retombe simplement sur l'écran par défaut, comme n'importe quelle adresse dont
 * l'écran n'est pas rendu.
 *
 * Et il nuisait : `getPathFromState` RETOMBE SUR LE NOM DE LA ROUTE quand aucune adresse n'est
 * déclarée. Ne rien déclarer ne les cachait pas — ça exposait leurs noms internes dans la barre
 * d'adresse. `AcceptTerms` existant à deux niveaux d'imbrication, les deux noms se concaténaient et
 * un onglet s'est retrouvé sur `/AcceptTerms/AcceptTerms`. `Splash` donnait `/Splash`.
 *
 * LES ÉCRANS À PARAMÈTRES REQUIS N'EN ONT PAS NON PLUS, et cette règle a été apprise à la dure —
 * `CvImprove`, `CoachingBooking`, `CoachingReservation` en avaient une dans la première version de
 * cette carte. Une arrivée par URL ne porte aucun paramètre : `route.params.formule` levait pendant
 * le rendu, tout l'arbre React se démontait, et l'utilisateur obtenait UNE PAGE ENTIÈREMENT BLANCHE
 * — sans rien dans la console. Le pire symptôme de la série, et le plus difficile à diagnostiquer.
 *
 * La règle qui en découle : une adresse ne se donne qu'à un écran qui sait s'afficher SANS
 * paramètre. Les autres restent joignables par la navigation, qui les leur fournit.
 *
 * Les chemins sont en français, comme l'interface. Ils sont publics et durables : les changer
 * casse les liens partagés et les favoris.
 */
/**
 * ⚠️ POURQUOI CETTE CARTE EST TYPÉE À PART, PUIS CONVERTIE.
 *
 * Les types de react-navigation ne résolvent la liste de paramètres d'une pile imbriquée que sur
 * UN niveau. `Coaching`, directement sous la racine, est vérifié ; `Main → CVs` et `Main → Profile`
 * ne le sont pas, et leur `initialRouteName` s'y retrouve typé `undefined` — alors que la valeur
 * est bel et bien lue à l'exécution (mesuré : la pile passe de `["Interview"]` à
 * `["CvHub", "Interview"]`).
 *
 * La conversion porte sur la carte entière plutôt que sur chaque `initialRouteName` : un cast par
 * ligne se serait dilué dans le fichier et aurait fini par être recopié sans être compris. Les noms
 * de routes restent donc à vérifier à la main — c'est le prix, et il est énoncé ici.
 */
const CARTE = {
  screens: {
    Onboarding: {
      // Même règle que les autres piles : `/cgu` ou `/confidentialite` atteints par un lien
      // doivent pouvoir remonter. Sans parent, leur flèche de retour serait inerte.
      //
      // N'ENTRE PAS EN CONFLIT avec l'`initialRouteName` posé sur le `Stack.Navigator` lui-même
      // (cf. `OnboardingNavigator`) : celui-ci décide de la route de DÉPART quand aucune URL n'est
      // résolue — l'arrivée déconnectée sur une adresse protégée — tandis que celui-ci décide de ce
      // qu'on empile SOUS une adresse résolue.
      initialRouteName: 'Onboarding',
      screens: {
        Onboarding: 'bienvenue',
        Auth: 'connexion',
        Terms: 'cgu',
        Privacy: 'confidentialite',
      },
    },
    // Portes obligatoires : adresses discrètes, en minuscules comme les autres. Elles ne les rendent
    // pas atteignables — seul l'état de la porte décide —, elles empêchent la fuite des noms internes.
    AcceptTerms: {
      initialRouteName: 'AcceptTerms',
      screens: {
        AcceptTerms: 'acceptation-cgu',
        Terms: 'acceptation-cgu/cgu',
        Privacy: 'acceptation-cgu/confidentialite',
        Help: 'acceptation-cgu/aide',
      },
    },
    ProfileCompletion: 'complement-de-profil',
    Splash: 'chargement',
    NouveauMotDePasse: 'nouveau-mot-de-passe',

    Main: {
      screens: {
        CVs: {
          /**
           * SANS CETTE LIGNE, ARRIVER PAR UNE URL ENFERME.
           *
           * `getStateFromPath('/carriere/entretien')` rend une pile d'UN SEUL écran — mesuré :
           * `["Interview"]`. `goBack()` n'a alors rien à dépiler, la flèche de retour est inerte,
           * et la personne est bloquée sur l'écran qu'elle vient d'ouvrir. `initialRouteName`
           * demande à react-navigation de synthétiser le parent en dessous : `["CvHub",
           * "Interview"]`. Le retour redevient possible, et le fil d'Ariane est celui qu'on aurait
           * eu en naviguant depuis l'onglet.
           *
           * À déclarer pour CHAQUE pile qui expose des écrans profonds. L'oublier ne casse rien
           * au premier regard : l'écran demandé s'affiche correctement, et c'est seulement en
           * essayant d'en sortir qu'on découvre l'impasse.
           */
          initialRouteName: 'CvHub',
          screens: {
            CvHub: 'carriere',
            MyCv: 'carriere/mon-cv',
            CvList: 'carriere/cv',
            CvCreate: 'carriere/cv/creer',
            CvScore: 'carriere/cv/score',
            LinkedinAudit: 'carriere/linkedin',
            MyCoverLetter: 'carriere/lettre',
            CoverLetterList: 'carriere/lettres',
            CoverLetterCreate: 'carriere/lettres/creer',
            CoverLetterAnalyse: 'carriere/lettres/analyse',
            Interview: 'carriere/entretien',
          },
        },
        Offers: 'offres',
        News: 'actualites',
        Profile: {
          // Même raison que pour CVs : sans parent sous l'écran, la flèche de retour est inerte.
          initialRouteName: 'Profile',
          screens: {
            Profile: 'profil',
            PersonalInfo: 'profil/informations',
            Settings: 'profil/parametres',
            Notifications: 'profil/notifications',
            NotificationsSettings: 'profil/notifications/reglages',
            Help: 'profil/aide',
            Admin: 'profil/admin',
            Privacy: 'profil/confidentialite',
            Terms: 'profil/cgu',
            DataDeletion: 'profil/suppression-du-compte',
          },
        },
      },
    },
    Coaching: {
      // Idem : une réservation atteinte par URL doit pouvoir remonter à la page Coaching.
      initialRouteName: 'Coaching',
      screens: {
        Coaching: 'coaching',
      },
    },

    /**
     * RETOUR DE PAIEMENT — la seule adresse de ce fichier qu'un TIERS compose.
     *
     * Klarna, Bancontact ou une authentification bancaire ramènent le navigateur ici, avec leurs
     * propres paramètres de query. Trois règles en découlent, et les trois sont des conditions de
     * non-régression :
     *
     *  1. AUCUN PARAMÈTRE DE ROUTE. C'est la règle apprise à la dure plus haut — `CvImprove`,
     *     `CoachingBooking`, `CoachingReservation` en avaient, et une arrivée par URL levait
     *     pendant le rendu pour une PAGE ENTIÈREMENT BLANCHE sans rien dans la console. Ici le
     *     risque est maximal : l'adresse est composée par quelqu'un d'autre que nous.
     *  2. ELLE LIT LA QUERY, PAS LA ROUTE. `payment_intent_client_secret` et `redirect_status`
     *     sont posés par Stripe, hors de la carte de liens.
     *  3. ELLE EST PUBLIQUE (cf. `CHEMINS_PUBLICS`) : on revient de chez un tiers sans garantie
     *     que la session ait survécu au trajet.
     */
    PaiementRetour: 'coaching/paiement/retour',
  },
};

/**
 * Adresse ABSOLUE où Stripe doit ramener le navigateur, exigée par `confirmPayment` dès qu'un
 * moyen à redirection est possible — c'est-à-dire toujours, `allow_redirects: 'always'` étant
 * désormais explicite dans `coaching-payment`.
 *
 * Construite depuis `window.location.origin`, comme le retour OAuth : aucune valeur figée, sans
 * quoi une préproduction renverrait vers la production. Hors du web, elle n'est pas utilisée pour
 * rediriger — la feuille native gère son retour seule — mais le SDK réclame une chaîne, d'où le
 * schéma applicatif en repli.
 */
export function URL_RETOUR_PAIEMENT(): string {
  // ⚠️ `typeof window === 'undefined'` NE SUFFIT PAS : React Native définit un `window` global
  // SANS `location`. Écrit ainsi, cet appel levait sur mobile — donc faisait échouer TOUT paiement
  // natif, la plateforme qui encaisse aujourd'hui. Attrapé par les tests de l'écran de
  // réservation, qui ont vu `coaching_paiement_exception` là où ils attendaient un refus.
  const origine = typeof window === 'undefined' ? undefined : window.location?.origin;
  if (!origine) return 'hormeia://coaching/paiement/retour';
  return `${origine}/coaching/paiement/retour`;
}

export const LIENS_WEB: LinkingOptions<RootStackParamList> = {
  // Sur le web, react-navigation lit `window.location` ; le préfixe ne sert qu'à satisfaire le
  // contrat de la prop et à ancrer la résolution sur l'origine courante.
  prefixes: [],
  config: CARTE as LinkingOptions<RootStackParamList>['config'],
};

/**
 * Chemins atteignables SANS session. Tout le reste est protégé, et arriver dessus déconnecté doit
 * mener à la connexion puis revenir — voir `destinationWeb`.
 *
 * `auth/callback`, `auth/confirm` et `auth/reset` en font partie : ce sont les retours d'authentification, par
 * définition parcourus alors qu'aucune session n'existe encore.
 */
export const CHEMINS_PUBLICS = [
  '/',
  '/bienvenue',
  '/connexion',
  '/cgu',
  '/confidentialite',
  '/auth/callback',
  '/auth/confirm',
  '/auth/reset',
  // Retour de paiement : on revient de chez Klarna ou d'une authentification bancaire, sans
  // garantie que la session ait survécu au trajet. Sans cette ligne, `destinationWeb` mémoriserait
  // l'adresse et enverrait vers la connexion — l'utilisateur perdrait le résultat de son paiement
  // derrière un formulaire, au moment précis où il veut savoir s'il a payé.
  '/coaching/paiement/retour',
];
